import {
  AutoComplete,
  AutoCompleteMulti,
  DateInput,
  EstablishmentAutoComplete,
  FormInput,
  IEstablishment,
  ISelectType,
  SwitchInput,
  useFetch,
  useFormContext,
} from "@4uhub/lib4uhub";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import MainApi from "../../../../../../services/mainApi.service";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Divider,
  Grid,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  listGenerics,
  listMainGenerics,
} from "../../../../../../services/generic.service";
import { PhysiciansAutoComplete } from "./PhysiciansAutoComplete";
import { InsuranceTypesAutoComplete } from "./InsuranceTypeAutoComplete";
import { useWatch } from "react-hook-form";
import { TESignRuleForm } from "../esignRuleSchema";
import {
  getEsignAutomationLocalsByAutomationCode,
  getEsignAutomationLocationsByAutomationId,
} from "../../../../../../services/esignAutomationLocals.service";
import EstablishmentsService from "../../../../../../services/establishments.service";
import { AttendaceTypeAutoComplete } from "./AttendaceTypeAutoComplete";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { TASY_AUTOMATION_CODES } from "./automationCodes";
import { AuthorizationFields } from "./AuthorizationFields";
import { InsurancesAutoComplete } from "./InsurancesAutoComplete";

const ESIGN_AUTOMATION_ROUTE =
  process.env.REACT_APP_4UESIGN + "/api/v1/ESignAutomation/";
const esignAutomationService = new MainApi<ISelectType>(ESIGN_AUTOMATION_ROUTE);

const ESIGN_AUTOMATION_REQUIREDS =
  process.env.REACT_APP_4UESIGN + "/api/v1/EsignAutomationSignatureRequireds/";
const esignAutomationSignatureRequiredsService = new MainApi<ISelectType>(
  ESIGN_AUTOMATION_REQUIREDS,
);

const establishmentListService = new EstablishmentsService();

const translationPath = "4uESign.page.automations.automationRules.";

export const GeneralTab = memo(() => {
  const { t } = useTranslation();
  const { control, setValue, getValues, resetField } =
    useFormContext<TESignRuleForm>();
  const [hasAdditionalInfo, setHasAdditionalInfo] = useState<boolean>(false);
  const [allowedLocations, setAllowedLocations] = useState<ISelectType[]>([]);
  const [establishmentsList, setEstablishmentsList] = useState<
    IEstablishment[]
  >([]);

  const theme = useTheme();

  const { sendRequest } = useFetch(getEsignAutomationLocalsByAutomationCode);
  const { sendRequest: getAllowedLocations } = useFetch(
    getEsignAutomationLocationsByAutomationId,
  );
  const { sendRequest: getSignOnlyGenerics } = useFetch(listGenerics);
  const { sendRequest: getEstablishments } = useFetch(
    establishmentListService.masterList,
  );

  const fetchEstablishments = useCallback(async () => {
    const { data, success } = await getEstablishments(null);
    if (data && success) {
      setEstablishmentsList(data);
    }
  }, [getEstablishments]);

  const esignSignOnlyParam = useMemo(() => ({ Identifier: "SignOnly" }), []);

  useEffect(() => {
    const setDefaultSignOnly = async () => {
      if (!getValues("signOnly")) {
        const { data, success } = await getSignOnlyGenerics(esignSignOnlyParam);

        if (success && Array.isArray(data)) {
          const defaultOption = data.find(
            (option: ISelectType) => option.code === "1",
          );

          if (defaultOption) {
            setValue("signOnly", defaultOption, {
              shouldValidate: true,
              shouldDirty: true,
            });
          }
        }
      }
    };

    setDefaultSignOnly();
  }, [getValues, getSignOnlyGenerics, esignSignOnlyParam, setValue]);

  useEffect(() => {
    fetchEstablishments();
  }, [fetchEstablishments]);

  const selectedAutomation = useWatch({ control, name: "esignAutomation" });

  const selectedLocation = useWatch({ control, name: "esignLocationSaveFile" });

  const signOnlyField = useWatch({ control, name: "signOnly" });

  const requiredSignatures = useWatch({
    control,
    name: "requiredSignatures",
  });

  const requiresTechnicalManager = requiredSignatures?.some(
    (signature) => signature.code === "4",
  );

  useEffect(() => {
    if (!requiresTechnicalManager && getValues("technicalManagerCode")) {
      setValue("technicalManagerCode", null, { shouldDirty: true });
    }
  }, [getValues, requiresTechnicalManager, setValue]);

  const isTasyAutomation = useMemo(() => {
    return TASY_AUTOMATION_CODES.includes(selectedAutomation?.code ?? "");
  }, [selectedAutomation]);

  const automationId = useMemo(
    () => ({ EsignAutomationId: selectedAutomation?.id }),
    [selectedAutomation],
  );

  useEffect(() => {
    if (signOnlyField?.code === "1") {
      setValue("signOnlyPeriod", 1, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [signOnlyField?.code, setValue]);

  const handleAutomationChange = useCallback(
    (value: ISelectType | null) => {
      if (value?.id === selectedAutomation?.id) return;

      resetField("esignLocationSaveFile", { defaultValue: undefined });
      setValue("fileTypeCodeIntegrationId", "");
      setValue("isAdditionalDataRequired", false);
      setHasAdditionalInfo(false);
    },
    [resetField, selectedAutomation?.id, setValue],
  );

  const fetchAllowedLocations = useCallback(
    async (esignAutomationId: string) => {
      const { data, success } = await getAllowedLocations({
        EsignAutomationId: esignAutomationId,
      });

      if (success && Array.isArray(data)) {
        setAllowedLocations(data);
        return;
      }

      setAllowedLocations([]);
    },
    [getAllowedLocations],
  );

  useEffect(() => {
    if (!selectedAutomation?.id) {
      setAllowedLocations([]);
      return;
    }

    fetchAllowedLocations(selectedAutomation.id);
  }, [fetchAllowedLocations, selectedAutomation?.id]);

  const handleFetchAdditionalInfo = useCallback(async () => {
    if (!selectedAutomation?.id || !selectedLocation?.id) {
      setHasAdditionalInfo(false);
      setValue("isAdditionalDataRequired", false);

      if (!selectedLocation?.id) {
        setValue("fileTypeCodeIntegrationId", "");
      }

      return;
    }

    const { data, success } = await sendRequest({
      EsignAutomationId: selectedAutomation.id,
      EsignLocationSaveFileId: selectedLocation.id,
    });

    if (data && success) {
      const isRequired = data.toString().toUpperCase() === "TRUE";
      setHasAdditionalInfo(isRequired);

      setValue("isAdditionalDataRequired", isRequired);
      if (!isRequired) {
        setValue("fileTypeCodeIntegrationId", "");
      }
    }
  }, [
    selectedAutomation,
    selectedLocation,
    setHasAdditionalInfo,
    setValue,
    sendRequest,
  ]);

  useEffect(() => {
    handleFetchAdditionalInfo();
  }, [handleFetchAdditionalInfo]);

  return (
    <Grid
      container
      spacing={2}
      p={2}
      sx={{
        backgroundColor: theme.palette.background.paper,
        borderRadius: 1,
        height: "100%",
        overflowY: "auto",
        alignContent: "flex-start",
      }}
    >
      {isTasyAutomation && (
        <Grid item xs={12}>
          <Alert severity="warning">
            {t(translationPath + "tasy_automation_warning") ||
              "Esta automação irá agrupar todas as regras referentes a ficha de atendimento para coletar a assinatura uma única vez do paciente, então se tiver várias regras irá coletar uma única assinatura do paciente."}
          </Alert>
        </Grid>
      )}

      <Grid item xs={12} sm={12} textAlign={"end"}>
        <SwitchInput
          name="saveQrCode"
          label={t(translationPath + "qrCodeEnable")}
        />
        <SwitchInput name="enable" label={t(translationPath + "enable")} />
      </Grid>

      <Grid item xs={12} sm={5}>
        <FormInput
          name={`name`}
          label={t(translationPath + "name")}
          size="small"
          fullWidth
          autoFocus
        />
      </Grid>

      <Grid item xs={12} sm={5}>
        <AutoComplete
          name="esignAutomation"
          label={t(translationPath + "esign_automation")}
          getOptionLabel={(option) => option.name}
          onValueChange={handleAutomationChange}
          size="small"
          fullWidth
          clearIcon={false}
          request={esignAutomationService.list}
        />
      </Grid>

      <Grid item xs={12} sm={2}>
        <FormInput
          name="priority"
          label={t(translationPath + "priority")}
          size="small"
          fullWidth
          type="number"
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <DateInput
          name={`initialDate`}
          label={t(translationPath + "initial_date")}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <DateInput name={`endDate`} label={t(translationPath + "end_date")} />
      </Grid>

      <Grid item xs={12} sm={signOnlyField?.code !== "2" ? 12 : 6}>
        <AutoComplete
          name="signOnly"
          label={t(translationPath + "signOnly") || "Filtro de assinatura"}
          getOptionLabel={(option) => option.name}
          size="small"
          fullWidth
          clearIcon={false}
          request={listMainGenerics}
          params={esignSignOnlyParam}
        />
      </Grid>

      {signOnlyField?.code === "2" && (
        <Grid item xs={12} sm={6}>
          <FormInput
            name="signOnlyPeriod"
            label={t(translationPath + "periodInDays")}
            size="small"
            fullWidth
            type="number"
            InputProps={{
              endAdornment: (
                <Tooltip title="Serão assinados somente os documentos que foram gerados em datas que respeitem esses dias onde o médico trabalhou">
                  <InfoOutlinedIcon
                    sx={{ fontSize: 18, color: "action.active", mr: 1 }}
                  />
                </Tooltip>
              ),
            }}
          />
        </Grid>
      )}

      <Grid item xs={12}>
        <FormInput
          size="small"
          multiline
          name="observation"
          fullWidth
          minRows={4}
          label={t(translationPath + "observation")}
        />
      </Grid>

      <AuthorizationFields />

      <PhysiciansAutoComplete />

      {isTasyAutomation && (
        <>
          <Grid item xs={12} sm={12}>
            <EstablishmentAutoComplete
              multiple
              name={"esignEstablishments"}
              label={t(translationPath + "establishment")}
              establishments={establishmentsList}
              disableEstablishmentOption
            />
          </Grid>

          <AttendaceTypeAutoComplete />
          <InsuranceTypesAutoComplete />
        </>
      )}

      <InsurancesAutoComplete />

      <Grid item xs={12} sm={12} mt={2}>
        <Divider sx={{ width: "100%", marginBottom: 2 }} />
        <Typography variant="h6">
          {t(translationPath + "file_generation")}
        </Typography>
      </Grid>

      <Grid item xs={12} sm={12}>
        <AutoComplete
          name="esignLocationSaveFile"
          label={
            t(translationPath + "esignStoragePlace") || "Local de armazenamento"
          }
          getOptionLabel={(option) => option.name}
          size="small"
          fullWidth
          clearIcon={false}
          disabled={!selectedAutomation?.id}
          options={allowedLocations}
        />
      </Grid>

      {hasAdditionalInfo && (
        <Grid item sm={12}>
          <Grid container spacing={1} alignItems="center">
            <Grid item xs={selectedLocation?.description ? 6 : 12}>
              <FormInput
                name="fileTypeCodeIntegrationId"
                label={
                  t(translationPath + "additionalData") ||
                  "Dado adicional para salvar o arquivo"
                }
                size="small"
                fullWidth
              />
            </Grid>
            {selectedLocation?.description && (
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">
                  {selectedLocation.description}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Grid>
      )}

      <Grid item xs={12} sm={12}>
        <AutoCompleteMulti
          name="requiredSignatures"
          label={
            t(translationPath + "requiredSignatures") || "Assinaturas exigidas"
          }
          getOptionLabel={(option) => option.name}
          size="small"
          fullWidth
          request={esignAutomationSignatureRequiredsService.list}
          params={automationId}
        />
      </Grid>

      {requiresTechnicalManager && (
        <Grid item xs={12} sm={12}>
          <FormInput
            name="technicalManagerCode"
            label={t(translationPath + "technicalManagerCode")}
            size="small"
            fullWidth
            inputProps={{ maxLength: 50 }}
          />
        </Grid>
      )}
    </Grid>
  );
});
